import type {
  AccountingHistoryDay,
  AccountingSettlementResult,
  AccountingValue,
} from '@pokerroom/types';

/** Weekly-ledger reading order: the five comparable income items, then beverage separately. */
export const coreCategories = [
  'MEMBERSHIP',
  'MONTHLY_PLAN',
  'RETAINED_Z',
  'VENUE_FEE',
  'ACTIVITY_FEE_1',
  'ACTIVITY_FEE_2',
] as const;

const toCents = (value: string) => {
  const match = /^(-)?(\d+)(?:\.(\d{1,2}))?$/.exec(value.trim());
  if (!match) return null;
  const cents = BigInt(match[2]!) * 100n + BigInt((match[3] ?? '').padEnd(2, '0'));
  return match[1] ? -cents : cents;
};
const fromCents = (cents: bigint) => {
  const sign = cents < 0n ? '-' : '';
  const abs = cents < 0n ? -cents : cents;
  return `${sign}${abs / 100n}.${(abs % 100n).toString().padStart(2, '0')}`;
};

/** Sums display values without turning a missing source into zero. */
export function sumValues(
  values: AccountingValue[],
  unit: AccountingValue['unit'],
): AccountingValue {
  let total = 0n,
    known = 0;
  for (const v of values) {
    const cents = v.value === null ? null : toCents(v.value);
    if (cents === null) continue;
    total += cents;
    known++;
  }
  if (!known) return { unit, status: 'UNAVAILABLE', value: null };
  const complete = known === values.length && values.every((v) => v.status === 'READY');
  return { unit, status: complete ? 'READY' : 'PARTIAL', value: fromCents(total) };
}

export const coreSubtotal = (day: AccountingHistoryDay) =>
  sumValues(
    coreCategories.map((c) => day.categories[c]),
    'TWD',
  );

/** Days that have not happened yet carry no figures and stay out of period totals. */
const counted = (days: AccountingHistoryDay[]) => days.filter((d) => d.status !== 'NOT_YET');

export function periodIncome(days: AccountingHistoryDay[]) {
  const rows = counted(days);
  return {
    categories: Object.fromEntries(
      [...coreCategories, 'BEVERAGE' as const].map((c) => [
        c,
        sumValues(
          rows.map((d) => d.categories[c]),
          'TWD',
        ),
      ]),
    ) as Record<(typeof coreCategories)[number] | 'BEVERAGE', AccountingValue>,
    core: sumValues(rows.map(coreSubtotal), 'TWD'),
    income: sumValues(
      rows.map((d) => d.income),
      'TWD',
    ),
  };
}

const sumAmounts = (values: string[]) => {
  let total = 0n;
  for (const v of values) total += toCents(v) ?? 0n;
  return fromCents(total);
};

/**
 * One row per game, from the complete settlement list (not a paged journal). The API sends
 * no participants unless the confirmation is VALID, so other rows carry unknown figures.
 */
export function gameRows(settlements: AccountingSettlementResult[]) {
  return settlements.map((s) => {
    const available = s.status === 'VALID';
    const total = (pick: (p: AccountingSettlementResult['participants'][number]) => string) =>
      available ? sumAmounts(s.participants.map(pick)) : null;
    return {
      tournamentId: s.tournamentId,
      sessionLabel: s.sessionLabel,
      status: s.status,
      unverified: s.reasons.length > 0 || (s.summary?.reasons.length ?? 0) > 0,
      available,
      people: available ? (s.summary?.participantCount ?? s.participants.length) : null,
      raCount: available ? (s.summary?.raCount ?? null) : null,
      confirmedFeeAndZ: available ? s.summary?.confirmedFeeAndZ : undefined,
      venueFee: available ? s.summary?.venueFee : undefined,
      expenseTotal: available ? s.summary?.expenseTotal : undefined,
      venueFeeNet: available ? s.summary?.venueFeeNet : undefined,
      activityFee1: available ? s.summary?.activityFee1 : undefined,
      activityFee2: available ? s.summary?.activityFee2 : undefined,
      retainedZ: available ? s.summary?.retainedZ : undefined,

      posted: available
        ? s.participants.filter((p) => p.financialStatus === 'POSTED').length
        : null,
      grossAward: total((p) => p.grossAward),
      zPoints: total((p) => p.zPoints),
      netAward: total((p) => p.netAward),
    };
  });
}

export const pendingMembers = (settlements: AccountingSettlementResult[]) =>
  settlements.reduce(
    (n, s) => n + s.participants.filter((p) => p.financialStatus === 'PENDING').length,
    0,
  );
